import React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { withRouter } from "../../../../../util/with-router";
import { PROJECT_MANAGEMENT, PROJECT_BUDGET } from "../../../../../constants/route-paths";
import { getLaborCost } from "../../../../../actions/dashboard.action";
import { getProject, getProjectSummary, getOptions, getvendorsPOList } from "../../../../../actions/project-management.action";
import { getResourceAllocations } from "../../../../../actions/timesheet.action";
import { getUserList } from "../../../../../actions/user-management.action";
import { getGlobalSetting } from "../../../../../actions/global-setting.action";
import Button from 'react-bootstrap/Button';
import "../budget.scss";
import "../../../../../components/cost-breakdown/breakdown.scss";
import ViewPOModal from "../../../../../components/cost-breakdown/view-po-modal";
import ViewInvoiceModal from "../../../../../components/cost-breakdown/view-invoice-modal";
import ViewWOModal from "../../../../../components/cost-breakdown/view-wo-modal";

// Hooks
import { useCostBreakdownParams } from "./hooks/useCostBreakdownParams";
import { useCostBreakdownModals } from "./hooks/useCostBreakdownModals";
import { useCostBreakdownApi } from "./hooks/useCostBreakdownApi";

// Components
import CostBreakdownHeader from "./components/CostBreakdownHeader";
import CostBreakdownContent from "./components/CostBreakdownContent";

// Utils
import { getStagePath, formatHours } from "./utils/costBreakdownHelpers";
import { getAllocationCost, getTotalCost } from "./utils/costBreakdownCalculations";

/**
 * CostBreakdown - Main container component for cost breakdown display
 * 
 * SUMMARY:
 * Orchestrates cost breakdown data fetching, state management, and UI rendering.
 * Supports both stage-level and task-level cost breakdowns for material, labor,
 * subcontractor, and combined cost types.
 */
const CostBreakdown = (props) => {
  const navigate = useNavigate();

  // Hooks
  const params = useCostBreakdownParams(props);
  const { modals, selected, handlers } = useCostBreakdownModals();
  const {
    project,
    task,
    stage,
    costDetails,
    isLoading,
    costTypeLabel,
    resourceAllocations,
    stageHierarchy,
    globalSettings
  } = useCostBreakdownApi(params, props);

  // Utilities
  const stagePath = getStagePath(stageHierarchy, task, stage, params.isStageBreakdown);
  const totalCost = getTotalCost(costDetails);

  // Create wrapped functions for calculations that need globalSettings
  const getAllocationCostWithSettings = (allocation) => {
    return getAllocationCost(allocation, globalSettings);
  };

  const navigateBack = () => {
    navigate(`/${PROJECT_MANAGEMENT}/${PROJECT_BUDGET}`.replace(':projectId', params.projectId));
  };

  // Early return if projectId is missing
  if (!params.projectId) {
    return (
      <div className="purchase-order-block">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              <strong>Error:</strong> Project ID is missing. Please navigate from the budget page.
              <br />
              <small>
                Debug info: projectId={String(params.projectId)}, costType={String(params.costType)},
                taskIdParam={String(params.taskIdParam)}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-order-block">
      <CostBreakdownHeader
        project={project}
        costTypeLabel={costTypeLabel}
        costType={params.costType}
        projectId={params.projectId}
      />

      <div className="row mb-4">
        <div className="col-12">
          <CostBreakdownContent
            isLoading={isLoading}
            costType={params.costType}
            costDetails={costDetails}
            resourceAllocations={resourceAllocations}
            costTypeLabel={costTypeLabel}
            stagePath={stagePath}
            getAllocationCost={getAllocationCostWithSettings}
            formatHours={formatHours}
            handleViewPO={handlers.handleViewPO}
            handleViewWO={handlers.handleViewWO}
            handleViewInvoice={handlers.handleViewInvoice}
            totalCost={totalCost}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 d-flex justify-content-end">
          <Button onClick={navigateBack} className="secondarybtn">
            Back to Budget
          </Button>
        </div>
      </div>

      {/* Modals */}
      <ViewPOModal
        show={modals.showPOModal}
        onHide={handlers.closePOModal}
        poId={selected.selectedPOId}
      />

      <ViewWOModal
        show={modals.showWOModal}
        onHide={handlers.closeWOModal}
        woId={selected.selectedWOId}
      />

      <ViewInvoiceModal
        show={modals.showInvoiceModal}
        onHide={handlers.closeInvoiceModal}
        invoiceData={selected.selectedInvoiceData}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.userData,
  projectSummary: state.projectSummary,
});

const mapDispatchToProps = (dispatch) => ({
  getProject: bindActionCreators(getProject, dispatch),
  getOptions: bindActionCreators(getOptions, dispatch),
  getProjectSummary: (data) => dispatch(getProjectSummary(data)),
  getLaborCost: bindActionCreators(getLaborCost, dispatch),
  getvendorsPOList: bindActionCreators(getvendorsPOList, dispatch),
  getResourceAllocations: bindActionCreators(getResourceAllocations, dispatch),
  getUsersList: bindActionCreators(getUserList, dispatch),
  getGlobalSettings: bindActionCreators(getGlobalSetting, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CostBreakdown));
